import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Select,
  TextField,
  Button,
  Container,
  Typography,
  Box,
  InputLabel,
  MenuItem,
  FormControl,
  Autocomplete,
  CircularProgress,
  FormHelperText,
} from "@mui/material";
import { apiRequest } from "../components/Config";
import ReCAPTCHA from "react-google-recaptcha";
import InputAdornment from "@mui/material/InputAdornment";
import { Checkbox, FormControlLabel } from "@mui/material";
import Alerts from "../components/Alerts";

export default function RegisterSMS() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [voivodeshipList, setVoivodeshipList] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [show, setShow] = useState(false);
  const [showApiResponse, setShowApiResponse] = useState(false);
  const ClearApiResponse = () => {
    setShowApiResponse(false);
  };
  // Walidacja Step 1
  const validationSchemaStep1 = Yup.object({
    user: Yup.string().required("Login jest wymagany"),
    fullName: Yup.string().required("Nazwisko i imię są wymagane"),
    phone: Yup.string().required("Telefon jest wymagany"),
    birthyear: Yup.number()
      .typeError("Podaj poprawny rok")
      .min(1960, "Podaj poprawny rok")
      .max(new Date().getFullYear() - 28, "Musisz mieć minimum 28 lat")
      .required("Rok urodzenia jest wymagany"),
    password: Yup.string()
      .min(6, "Hasło musi mieć min. 6 znaków")
      .required("Hasło jest wymagane"),
    passwordrepeat: Yup.string()
      .oneOf([Yup.ref("password"), null], "Hasła muszą być identyczne")
      .required("Powtórzenie hasła jest wymagane"),
    voivodeship: Yup.string().required("Wybierz województwo"),
    city: Yup.object().nullable().required("Wybierz miasto z listy"),
    rodo: Yup.boolean().oneOf(
      [true],
      "Musisz wyrazić zgodę na przetwarzanie danych",
    ),
  });

  // Walidacja Step 2
  const validationSchemaStep2 = Yup.object({
    code: Yup.string()
      .length(6, "Kod musi mieć 6 cyfr")
      .required("Kod jest wymagany"),
  });

  useEffect(() => {
    const fetchVoivodeships = async () => {
      try {
        const response = await apiRequest("voivodeship");
        setVoivodeshipList(response);
      } catch (error) {
        console.error("Błąd pobierania województw:", error.message);
      }
    };
    fetchVoivodeships();
  }, []);

  const [formData, setFormData] = useState({
    user: "",
    fullName: "",
    phone: "",
    birthyear: "",
    password: "",
    passwordrepeat: "",
    voivodeship: "",
    city: null,
    rodo: false,
  });

  return (
    <Container maxWidth="sm">
      <Box mt={5} mb={5}>
        <h3>REJESTRACJA OPIEKUNKI</h3>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: "bold" }}>
          Zarejestruj swoje konto
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Zalecany format: pierwsza litera imienia i nazwisko (np. <i>anowak</i>
          ).
        </Typography>

        {step === 1 && (
          <Formik
            enableReinitialize={true}
            initialValues={formData}
            validationSchema={validationSchemaStep1}
            onSubmit={async (values) => {
              if (!captchaToken) {
                alert("Proszę potwierdzić, że nie jesteś robotem.");
                return;
              }

              setFormData(values);
              setPhone(values.phone);
              try {
                const payload = {
                  ...values,
                  city: values.city.id,
                  captcha: captchaToken,
                };
                console.log("Payload: ", payload);
                const answer = await apiRequest("register", "POST", payload);
                console.log("Payload: ", payload);
                console.log("Answer: ", answer);
                setPhone(values.phone);
                setStep(2);
              } catch (e) {
                alert(
                  "Błąd rejestracji. Być może login lub telefon jest już zajęty.",
                );
              }
            }}
          >
            {({ values, handleChange, errors, touched, setFieldValue }) => {
              // eslint-disable-next-line react-hooks/rules-of-hooks
              useEffect(() => {
                const fetchCities = async () => {
                  if (values.voivodeship) {
                    setLoadingCities(true);
                    try {
                      const response = await apiRequest(
                        `cities/${values.voivodeship}`,
                      );

                      setCities(Array.isArray(response) ? response : []);
                    } catch (error) {
                      console.error("Błąd pobierania miast:", error.message);
                      setCities([]);
                    } finally {
                      setLoadingCities(false);
                    }
                  } else {
                    setCities([]);
                  }
                };
                fetchCities();
              }, [values.voivodeship]);
              const currentYear = new Date().getFullYear();

              const birthYears = [];
              for (let y = currentYear - 28; y >= 1960; y--) {
                birthYears.push(y);
              }
              return (
                <Form>
                  <TextField
                    fullWidth
                    label="Login"
                    name="user"
                    margin="normal"
                    value={values.user}
                    onChange={handleChange}
                    error={touched.user && Boolean(errors.user)}
                    helperText={touched.user && errors.user}
                  />
                  <TextField
                    fullWidth
                    label="Nazwisko i Imię"
                    name="fullName"
                    margin="normal"
                    value={values.fullName}
                    onChange={handleChange}
                    error={touched.fullName && Boolean(errors.fullName)}
                    helperText={touched.fullName && errors.fullName}
                  />
                  <TextField
                    fullWidth
                    label="Telefon"
                    name="phone"
                    margin="normal"
                    value={values.phone}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/\D/g, "")
                        .slice(0, 9);
                      setFieldValue("phone", value);
                    }}
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">48</InputAdornment>
                      ),
                    }}
                  />
                  <FormControl
                    fullWidth
                    margin="normal"
                    error={touched.birthyear && Boolean(errors.birthyear)}
                  >
                    <InputLabel id="birthyear-label">Rok urodzenia</InputLabel>

                    <Select
                      labelId="birthyear-label"
                      name="birthyear"
                      value={values.birthyear}
                      label="Rok urodzenia"
                      onChange={handleChange}
                    >
                      {birthYears.map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>

                    {touched.birthyear && (
                      <FormHelperText>{errors.birthyear}</FormHelperText>
                    )}
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Hasło"
                    type="password"
                    name="password"
                    margin="normal"
                    value={values.password}
                    onChange={handleChange}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                  />

                  <TextField
                    fullWidth
                    label="Powtórz hasło"
                    type="password"
                    name="passwordrepeat"
                    margin="normal"
                    value={values.passwordrepeat}
                    onChange={handleChange}
                    error={
                      touched.passwordrepeat && Boolean(errors.passwordrepeat)
                    }
                    helperText={touched.passwordrepeat && errors.passwordrepeat}
                  />

                  {/* Wybór Województwa */}
                  <FormControl
                    fullWidth
                    margin="normal"
                    error={touched.voivodeship && Boolean(errors.voivodeship)}
                  >
                    <InputLabel id="voivodeship-label">Województwo</InputLabel>
                    <Select
                      labelId="voivodeship-label"
                      name="voivodeship"
                      value={values.voivodeship}
                      label="Województwo"
                      onChange={(e) => {
                        setFieldValue("voivodeship", e.target.value);
                        setFieldValue("city", null); // Reset miasta
                      }}
                    >
                      {voivodeshipList.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.nazwa}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.voivodeship && (
                      <FormHelperText>{errors.voivodeship}</FormHelperText>
                    )}
                  </FormControl>

                  {/* Autocomplete dla Miasta */}
                  <Autocomplete
                    options={cities}
                    getOptionLabel={(option) => option.nazwa || ""}
                    value={values.city}
                    disabled={!values.voivodeship}
                    loading={loadingCities}
                    onChange={(_, newValue) => setFieldValue("city", newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Miasto zamieszkania"
                        margin="normal"
                        error={touched.city && Boolean(errors.city)}
                        helperText={touched.city && errors.city}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCities ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="rodo"
                        checked={values.rodo}
                        onChange={handleChange}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Wyrażam zgodę na przetwarzanie moich danych osobowych
                        przez administratora serwisu twojaopiekunka.pl w celu
                        utworzenia i prowadzenia mojego konta oraz umożliwienia
                        kontaktu ze strony osób poszukujących opieki, zgodnie z{" "}
                        <a href="/polityka-prywatnosci" target="_blank">
                          Polityką prywatności
                        </a>
                        .
                      </Typography>
                    }
                  />

                  {touched.rodo && errors.rodo && (
                    <FormHelperText error>{errors.rodo}</FormHelperText>
                  )}
                  <Box mt={2} mb={2} display="flex" justifyContent="center">
                    <ReCAPTCHA
                      key="my-captcha"
                      sitekey="6Ld8dZcsAAAAAI2kdXLZVwduM4ydXysrZRJju4qo"
                      onChange={(token) => setCaptchaToken(token)}
                    />
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{ mt: 2 }}
                  >
                    Zarejestruj i wyślij SMS
                  </Button>
                </Form>
              );
            }}
          </Formik>
        )}

        {step === 2 && (
          <Formik
            initialValues={{ code: "" }}
            validationSchema={validationSchemaStep2}
            onSubmit={async (values) => {
              try {
                await apiRequest("verify-sms", "POST", {
                  phone,
                  code: values.code,
                });
                alert("Konto aktywowane pomyślnie!");
                navigate("/login");
              } catch (e) {
                alert("Nieprawidłowy kod weryfikacyjny");
              }
            }}
          >
            {({ values, handleChange, errors, touched }) => (
              <Form>
                <Box textAlign="center" mb={3}>
                  <Typography variant="h6">Weryfikacja SMS</Typography>
                  <Typography color="textSecondary">
                    Wpisz 6-cyfrowy kod wysłany na numer: <b>{phone}</b>
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="Kod SMS"
                  name="code"
                  margin="normal"
                  value={values.code}
                  onChange={handleChange}
                  error={touched.code && Boolean(errors.code)}
                  helperText={touched.code && errors.code}
                  inputProps={{
                    maxLength: 6,
                    style: {
                      textAlign: "center",
                      letterSpacing: "5px",
                      fontSize: "1.5rem",
                    },
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  fullWidth
                  size="large"
                  sx={{ mt: 3 }}
                >
                  Potwierdź aktywację
                </Button>

                <Button fullWidth sx={{ mt: 1 }} onClick={() => setStep(1)}>
                  Wróć do edycji danych
                </Button>
              </Form>
            )}
          </Formik>
        )}
      </Box>
    </Container>
  );
}
